import { useCallback, useEffect, useRef } from "react";
import { atom, useRecoilState } from "recoil";
import { AzureApi } from "../system/api/AzureApi";
import { ErrorHandler } from "../system/ApiService";
import AlertModal from "../components/Common/AlertModal";

interface SasTokenState {
  sasUrl: string; // full SAS URL (for BlobServiceClient)
  tokenPart: string; // query string only (for appending to image URLs)
  expiresAt: number; // timestamp (ms)
}

const SAS_TOKEN_BUFFER_MS = 60 * 1000; // 만료 1분 전에 갱신

const SasTokenAtom = atom<SasTokenState>({
  key: "SasTokenAtom",
  default: {
    sasUrl: "",
    tokenPart: "",
    expiresAt: 0,
  },
});

// 모듈 레벨 변수: 중복 API 호출 방지
let fetchPromise: Promise<SasTokenState> | null = null;

function parseSasExpiry(sasUrl: string): number {
  try {
    const tokenPart = sasUrl.split("?")[1] || "";
    const params = new URLSearchParams(tokenPart);
    const se = params.get("se"); // SAS expiry parameter
    if (se) {
      return new Date(se).getTime();
    }
  } catch {
    // fallback: 9분 후 만료로 설정
  }
  return Date.now() + 9 * 60 * 1000;
}

function isTokenValid(state: SasTokenState): boolean {
  if (!state.sasUrl) return false;
  return Date.now() < state.expiresAt - SAS_TOKEN_BUFFER_MS;
}

function fetchAndParseSasToken(): Promise<SasTokenState> {
  // 이미 진행 중인 요청이 있으면 재사용 (중복 호출 방지)
  if (fetchPromise) return fetchPromise;

  fetchPromise = AzureApi.getSasToken()
    .then((res) => {
      const sasUrl: string = res.data.sasUrl;
      const tokenPart = sasUrl.split("?")[1] || "";
      const expiresAt = parseSasExpiry(sasUrl);
      return { sasUrl, tokenPart, expiresAt };
    })
    .finally(() => {
      fetchPromise = null;
    });

  return fetchPromise;
}

/**
 * SAS 토큰을 전역으로 관리하는 커스텀 훅
 *
 * - 유효한 토큰이 있으면 재사용
 * - 만료 1분 전에 자동 갱신 (타이머)
 * - 모바일 백그라운드 복귀 시 자동 체크
 * - 여러 컴포넌트에서 동시 호출해도 API 1회만 호출
 *
 * @returns { sasUrl, tokenPart, appendSas, refreshSasToken }
 * - sasUrl: BlobServiceClient에 전달할 전체 SAS URL
 * - tokenPart: 이미지 URL에 붙일 쿼리 스트링
 * - appendSas: URL에 SAS 토큰을 붙여주는 헬퍼 함수
 * - refreshSasToken: 업로드 전 수동 갱신 (만료 시에만 API 호출)
 */
export function useSasToken() {
  const [sasToken, setSasToken] = useRecoilState(SasTokenAtom);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 토큰 갱신 함수
  const refresh = useCallback(() => {
    fetchAndParseSasToken()
      .then((newToken) => setSasToken(newToken))
      .catch((err) => {
        const msg = ErrorHandler(err);
        AlertModal("msg", msg);
      });
  }, [setSasToken]);

  // 마운트 시 또는 토큰이 유효하지 않으면 갱신
  useEffect(() => {
    if (!isTokenValid(sasToken)) {
      refresh();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 만료 전 자동 갱신 타이머
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (sasToken.expiresAt > 0) {
      const msUntilRefresh = sasToken.expiresAt - SAS_TOKEN_BUFFER_MS - Date.now();

      if (msUntilRefresh > 0) {
        timerRef.current = setTimeout(() => {
          refresh();
        }, msUntilRefresh);
      }
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [sasToken.expiresAt, refresh]);

  // 모바일 백그라운드 복귀 시 토큰 체크
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !isTokenValid(sasToken)) {
        refresh();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [sasToken, refresh]);

  const appendSas = useCallback(
    (url: string) =>
      sasToken.tokenPart ? `${url}?${sasToken.tokenPart}` : url,
    [sasToken.tokenPart]
  );

  // 업로드 전 호출: 만료됐으면 갱신 후 새 sasUrl 반환
  const refreshSasToken = useCallback(async (): Promise<string> => {
    if (isTokenValid(sasToken)) {
      return sasToken.sasUrl;
    }
    const newToken = await fetchAndParseSasToken();
    setSasToken(newToken);
    return newToken.sasUrl;
  }, [sasToken, setSasToken]);

  return {
    sasUrl: sasToken.sasUrl,
    tokenPart: sasToken.tokenPart,
    appendSas,
    refreshSasToken,
  };
}
